import AffirmElement from "@shared/modules/payments/components/AffirmElement/AffirmElement";
import Icon from "@ui-kit/icons/Icon.web";
import Accordion from "@ui-kit/disclosure/Accordion/Accordion.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useTranslation } from "@shared/utils/internationalization";
import { useEffect, useState } from "react";
import type { UseFormReturn } from "react-hook-form";

import PublicCheckoutBillingAddressForm from "@app-contexts/public/shared/checkout/components/steps/review/delivery-methods/PublicCheckoutBillingAddressForm/PublicCheckoutBillingAddressForm.web";
import { type UsePublicBasketType } from "@app-contexts/public/features/basket/services/usePublicBasket";
import { PAYMENT_TYPE_OPTIONS_MAP } from "@shared/constants";
import ErrorBoundary from "@shared/components/application/other/ErrorBoundary/ErrorBoundary.web";
import PaymentTypeAccordion, {
  SelectedRadioSymbol,
  UnselectedRadioSymbol,
} from "../PaymentTypeAccordion.web";

/**
 * AccordionItem for PAYMENT_TYPE_OPTIONS_MAP.AFFIRM
 */
function AffirmPaymentType({
  onSelect,
  onValidationChange,
  isDisabled = false,
  value,
  addressForm,
  publicBasket,
  onBillingAddressChange,
}: {
  onSelect: (func: () => void) => void;
  onValidationChange: (isValid: boolean) => void;
  isDisabled: boolean;
  value?: string;
  addressForm: UseFormReturn;
  publicBasket: UsePublicBasketType;
  onBillingAddressChange: (billingAddress: {
    info_home_address_country: string;
  }) => void;
}): JSX.Element {
  const { t } = useTranslation("checkout");

  const [isAffirmMessageReady, setIsAffirmMessageReady] = useState(false);

  const [isAddressValid, setIsAddressValid] = useState(false);

  useEffect(() => {
    onValidationChange(isAddressValid);
  }, [isAddressValid]);

  const handleOnClick = (isExpanded: boolean) => () => {
    if (isExpanded) return;

    onSelect(async () => {
      await publicBasket.updatePaymentType(PAYMENT_TYPE_OPTIONS_MAP.AFFIRM.id, {
        shouldPersist: false,
      });
      await publicBasket.updatePaymentMethod(null, {
        shouldPersist: false,
      });
      // when switching to Affirm, remove refund protection token since prices will change.
      await publicBasket.save({ withProtectionToken: false });
    });
  };

  return (
    // Intentionally omit a fallback. This is because we want these to silent fail to a customer.
    // in contrast, manual card entry does not have a boundary and should propagate it's crashes up.
    <ErrorBoundary fallback={null}>
      <PaymentTypeAccordion.Item
        display={!isAffirmMessageReady ? "none" : "block"}
        animation="slideDown 0.5s ease-out forwards"
        disabled={isDisabled}
        value={value}
      >
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore */}
        {({
          isExpanded,
        }: {
          // eslint-disable-next-line react/no-unused-prop-types
          isExpanded: boolean;
        }) => {
          return (
            <>
              {/* Deviate from the PaymentTypeAccordion components for the button, due to custom styling with AffirmElement */}
              <Accordion.Trigger
                iconPosition="none"
                backgroundColor="bg"
                color="fg"
                _open={{
                  backgroundColor: "bg.muted",
                  color: "fg",
                }}
                onClick={handleOnClick(isExpanded)}
              >
                <Flex direction="column" width="100%">
                  <Flex direction="row" alignItems="center">
                    <Box mr="lg">
                      {isExpanded ? (
                        <SelectedRadioSymbol />
                      ) : (
                        <UnselectedRadioSymbol />
                      )}
                    </Box>
                    <Flex
                      direction="row"
                      justifyContent="space-between"
                      width="100%"
                    >
                      <Text lineHeight="32px" fontWeight={700}>
                        Affirm
                      </Text>

                      <Flex direction="row" gap="2xs">
                        <Icon.AffirmLogo
                          width="element.4xl"
                          height="element.2xl"
                        />
                      </Flex>
                    </Flex>
                  </Flex>
                  <Box mt={isAffirmMessageReady ? "8px" : "0px"}>
                    <AffirmElement
                      onElementReady={() => {
                        setIsAffirmMessageReady(true);
                      }}
                    />
                    <Text align="left" fontSize="xs" color="text.secondary">
                      {t("Additional fees may apply")}
                    </Text>
                  </Box>
                </Flex>
              </Accordion.Trigger>
              <PaymentTypeAccordion.Content>
                <PublicCheckoutBillingAddressForm
                  form={addressForm}
                  onValidFields={() => {
                    setIsAddressValid(true);
                  }}
                  onInvalidFields={() => {
                    setIsAddressValid(false);
                  }}
                  onBillingAddressChange={onBillingAddressChange}
                />
              </PaymentTypeAccordion.Content>
            </>
          );
        }}
      </PaymentTypeAccordion.Item>
    </ErrorBoundary>
  );
}

export default AffirmPaymentType;
